import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Selector nativo del sistema de diseño.
 *
 * Se usa el `<select>` del navegador (y no un menú propio) porque en móvil abre la rueda
 * nativa y porque el teclado y los lectores de pantalla ya lo entienden. Solo se le da el
 * mismo aspecto que `Input`: altura de 40px, radio de 10px, borde `line-strong` y foco con
 * halo azul, con una flecha a la derecha.
 *
 * Igual que los demás controles, se usa dentro de un `Field`.
 */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid = false, children, ...props },
  ref,
) {
  return (
    <div className="relative w-full">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-10 w-full appearance-none rounded-control border bg-surface pr-9 pl-3 text-body text-ink transition-colors',
          'focus:outline-none focus-visible:outline-none focus-visible:ring-4',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted',
          invalid
            ? 'border-danger focus-visible:border-danger focus-visible:ring-danger-soft'
            : 'border-line-strong focus-visible:border-brand-500 focus-visible:ring-brand-100',
          className,
        )}
        {...props}
      >
        {children}
      </select>

      <ChevronDown
        size={16}
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-ink-faint"
      />
    </div>
  );
});
